import {useState, useEffect, useCallback, useMemo} from 'react';
import {
	fetchWmsProducts,
	updateWmsWeight,
	bulkUpdateWmsWeights,
} from '../services/wmsWeightService';
import {isValidWeight, getActiveFilters, rowKey} from '../utils/helper';

const DEFAULT_LIMIT = 50;

export const useWmsWeight = () => {
	const [items, setItems] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(DEFAULT_LIMIT);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [inputPage, setInputPage] = useState('');
	const [showFilter, setShowFilter] = useState(true);
	const [filtersDirty, setFiltersDirty] = useState(false);

	// Weight edits pending save, keyed by price_type_id.
	const [editedWeights, setEditedWeights] = useState({});
	// Per-row save outcome, keyed by price_type_id: 'saving' | 'saved' | error text.
	const [rowStatus, setRowStatus] = useState({});
	const [isSaving, setIsSaving] = useState(false);
	const [saveSummary, setSaveSummary] = useState(null);

	const [filters, setFilters] = useState({
		search: '',
		has_weight: '',
		supplier_id: '',
		store_id: '',
		include_deleted: false,
	});

	const totalPages = useMemo(
		() => Math.max(1, Math.ceil(total / limit)),
		[total, limit]
	);

	const loadProducts = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const activeFilters = getActiveFilters({
				...filters,
				// Only send include_deleted when actually enabled — the endpoint
				// reads any truthy tri-state value as "include".
				include_deleted: filters.include_deleted ? true : '',
			});
			const response = await fetchWmsProducts({page, limit, ...activeFilters});

			if (!response || response.success !== true) {
				setItems([]);
				setTotal(0);
				setError(
					typeof response?.data === 'string'
						? response.data
						: 'Failed to fetch products. Please try again.'
				);
				return;
			}

			setItems(response.data?.items || []);
			setTotal(response.data?.total || 0);
			// A fresh page invalidates edits staged against the old rows.
			setEditedWeights({});
			setRowStatus({});
		} catch (err) {
			console.error('Error fetching WMS products:', err);
			setError('Failed to fetch products. Please try again.');
		} finally {
			setLoading(false);
		}
	}, [page, limit, filters]);

	useEffect(() => {
		loadProducts();
		// `filters` is read through loadProducts but deliberately not a trigger:
		// text filters apply on submit (filtersDirty), not on every keystroke.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, limit, filtersDirty]);

	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
			setPage(newPage);
		}
	};

	const handleLimitChange = (newLimit) => {
		setPage(1);
		setLimit(newLimit);
	};

	const handleFilterChange = (field, value, autoApply = false) => {
		setFilters((prev) => ({...prev, [field]: value}));
		if (autoApply) {
			setPage(1);
			setTimeout(() => setFiltersDirty((prev) => !prev), 0);
		}
	};

	const applyFilters = () => {
		setPage(1);
		setFiltersDirty((prev) => !prev);
	};

	const resetFilters = () => {
		setPage(1);
		setFilters({
			search: '',
			has_weight: '',
			supplier_id: '',
			store_id: '',
			include_deleted: false,
		});
		setFiltersDirty((prev) => !prev);
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter') applyFilters();
	};

	// ---- editing ----

	const handleWeightChange = (priceTypeId, value) => {
		setEditedWeights((prev) => ({...prev, [String(priceTypeId)]: value}));
		setRowStatus((prev) => {
			if (!prev[String(priceTypeId)]) return prev;
			const next = {...prev};
			delete next[String(priceTypeId)];
			return next;
		});
	};

	/**
	 * Rows whose input differs from the value already stored. Comparing against
	 * weight_grams (not the raw weight) keeps a kg-stored row from looking
	 * changed just because the input shows its gram equivalent.
	 */
	const dirtyRows = useMemo(() => {
		return items.filter((item) => {
			const edited = editedWeights[rowKey(item)];
			if (edited === undefined || edited === '') return false;
			const current = item.weight_grams;
			return String(parseFloat(edited)) !== String(current);
		});
	}, [items, editedWeights]);

	const invalidRows = useMemo(
		() => dirtyRows.filter((item) => !isValidWeight(editedWeights[rowKey(item)])),
		[dirtyRows, editedWeights]
	);

	const applyRowResult = (item, weight) => {
		setItems((prev) =>
			prev.map((row) =>
				row.price_type_id === item.price_type_id
					? {
						...row,
						weight,
						weight_unit: 'g',
						weight_grams: weight,
						has_weight: weight > 0,
					}
					: row
			)
		);
		setEditedWeights((prev) => {
			const next = {...prev};
			delete next[rowKey(item)];
			return next;
		});
	};

	/** Save a single row via the single-update endpoint. */
	const handleSaveRow = async (item) => {
		const key = rowKey(item);
		const raw = editedWeights[key];

		if (!isValidWeight(raw)) {
			setRowStatus((prev) => ({...prev, [key]: 'Weight must be a positive number'}));
			return;
		}

		const weight = parseFloat(raw);
		setRowStatus((prev) => ({...prev, [key]: 'saving'}));

		const response = await updateWmsWeight(item.price_type_id, weight);

		if (response?.success === true && response?.error === false) {
			applyRowResult(item, weight);
			setRowStatus((prev) => {
				const next = {...prev};
				delete next[key];
				return next;
			});
		} else {
			const message = typeof response?.data === 'string' ? response.data : 'Update failed';
			setRowStatus((prev) => ({...prev, [key]: message}));
			// Without a per-row status cell the banner is the only place a failed
			// save is legible, so route it there too.
			setSaveSummary({
				updated: 0,
				failed: 1,
				message: `${item.product_code || `Variant ${item.price_type_id}`}: ${message}`,
			});
		}
	};

	/**
	 * Commit the row when focus leaves the input.
	 *
	 * Skips clean rows so tabbing through the table does not fire a request per
	 * cell, and skips rows already in flight so Enter-then-blur cannot double-post.
	 */
	const handleWeightBlur = (item) => {
		const key = rowKey(item);
		if (rowStatus[key] === 'saving') return;
		if (!dirtyRows.some((row) => row.price_type_id === item.price_type_id)) return;
		handleSaveRow(item);
	};

	/**
	 * Save every dirty row in one bulk call.
	 *
	 * The endpoint returns 200 with per-entry failures in `data.errors`, so
	 * results are reconciled row by row rather than trusting the status code.
	 */
	const handleSaveAll = async () => {
		// Clicking the button blurs whatever input had focus, which already fires
		// a save for that row. Exclude in-flight rows so it is not posted twice.
		const pending = dirtyRows.filter((item) => rowStatus[rowKey(item)] !== 'saving');
		if (!pending.length || isSaving) return;

		if (invalidRows.length) {
			setRowStatus((prev) => {
				const next = {...prev};
				invalidRows.forEach((item) => {
					next[rowKey(item)] = 'Weight must be a positive number';
				});
				return next;
			});
			setSaveSummary({
				updated: 0,
				failed: invalidRows.length,
				message: `${invalidRows.length} row(s) have an invalid weight. Fix them before saving.`,
			});
			return;
		}

		setIsSaving(true);
		setSaveSummary(null);

		const payload = pending.map((item) => ({
			price_type_id: item.price_type_id,
			weight: parseFloat(editedWeights[rowKey(item)]),
		}));

		const response = await bulkUpdateWmsWeights(payload);

		// A transport/500 failure has no per-entry detail — surface it whole.
		if (!response || response.success !== true) {
			setSaveSummary({
				updated: 0,
				failed: payload.length,
				message: typeof response?.data === 'string' ? response.data : 'Bulk update failed',
			});
			setIsSaving(false);
			return;
		}

		const errors = response.data?.errors || [];
		const failedIds = new Set(
			errors
				.filter((e) => e.price_type_id !== undefined && e.price_type_id !== null)
				.map((e) => String(e.price_type_id))
		);

		setRowStatus((prev) => {
			const next = {...prev};
			errors.forEach((e) => {
				if (e.price_type_id !== undefined && e.price_type_id !== null) {
					next[String(e.price_type_id)] = e.error || 'Update failed';
				}
			});
			return next;
		});

		pending.forEach((item) => {
			if (!failedIds.has(rowKey(item))) {
				applyRowResult(item, parseFloat(editedWeights[rowKey(item)]));
			}
		});

		const updatedCount = payload.length - failedIds.size;
		setSaveSummary({
			updated: updatedCount,
			failed: failedIds.size,
			message: failedIds.size
				? `${updatedCount} weight(s) saved, ${failedIds.size} failed.`
				: `${updatedCount} weight(s) saved.`,
		});
		setIsSaving(false);
	};

	const handleDiscardAll = () => {
		setEditedWeights({});
		setRowStatus({});
		setSaveSummary(null);
	};

	return {
		// state
		items,
		total,
		totalPages,
		page,
		limit,
		loading,
		error,
		inputPage,
		showFilter,
		filters,
		editedWeights,
		rowStatus,
		isSaving,
		saveSummary,
		dirtyRows,
		invalidRows,

		// setters
		setInputPage,
		setShowFilter,
		setSaveSummary,

		// handlers
		loadProducts,
		handlePageChange,
		handleLimitChange,
		handleFilterChange,
		applyFilters,
		resetFilters,
		handleKeyPress,
		handleWeightChange,
		handleWeightBlur,
		handleSaveRow,
		handleSaveAll,
		handleDiscardAll,
	};
};
